import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { ToggleArrowIcon } from "@common/assets/toggleArrowIcon";
import { Calendar } from "@common/calendar";
import { SelectList } from "@common/selectList";
import { getMonthsTexts } from "../../utils";
import { Props } from "./types";
import styles from "./styles.scss";

function DateTimeInput({ date: d, input, prev, next, today }: Props) {
  const { t } = useTranslation();
  const months = getMonthsTexts(t);
  const userTimezone = DateTime.local().zoneName;
  const parsed = DateTime.fromISO(d, { zone: "utc" });
  const date = parsed.isValid
    ? { year: parsed.year, month: parsed.month - 1, day: parsed.day }
    : null;
  const now = DateTime.now().setZone(userTimezone);

  return (
    <SelectList<string>
      listClassName={styles.dateTimeInput}
      rootClassName={styles.root}
    >
      {({ select }) => ({
        input: (
          <div className={styles.inputBox}>
            <div
              className={styles.leftArrowWrapper}
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
            >
              <div className={styles.arrow}>
                <ToggleArrowIcon className={styles.leftArrow} />
              </div>
            </div>
            <h3 className={styles.title}>
              {date
                ? `${months[date.month]} ${date.year}`
                : `${months[now.month - 1]} ${now.year}`}
            </h3>
            <div
              className={styles.rightArrowWrapper}
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
            >
              <div className={styles.arrow}>
                <ToggleArrowIcon className={styles.rightArrow} />
              </div>
            </div>
            <div className={styles.calendarButton}>
              <svg
                className={styles.calendarIcon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 10H21M7 3V5M17 3V5M6.2 21H17.8C18.9201 21 19.4802 21 19.908 20.782C20.2843 20.5903 20.5903 20.2843 20.782 19.908C21 19.4802 21 18.9201 21 17.8V8.2C21 7.07989 21 6.51984 20.782 6.09202C20.5903 5.71569 20.2843 5.40973 19.908 5.21799C19.4802 5 18.9201 5 17.8 5H6.2C5.0799 5 4.51984 5 4.09202 5.21799C3.71569 5.40973 3.40973 5.71569 3.21799 6.09202C3 6.51984 3 7.07989 3 8.2V17.8C3 18.9201 3 19.4802 3.21799 19.908C3.40973 20.2843 3.71569 20.5903 4.09202 20.782C4.51984 21 5.07989 21 6.2 21Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        ),
        list: (
          <Calendar
            date={
              date
                ? date
                : {
                    year: now.year,
                    month: now.month - 1,
                    day: now.day,
                  }
            }
            editDate={({ year, month, day }) => {
              const nextDate = DateTime.utc(year, month + 1, day).toISO() ?? "";
              const sameDay = !!date && date.day === day;
              const sameIso = parsed.isValid && parsed.toISO() === nextDate;

              input(!sameDay && !sameIso ? select(nextDate) : nextDate);
            }}
            today={() => {
              today();
              select(d);
            }}
          />
        ),
      })}
    </SelectList>
  );
}

export { DateTimeInput };
